import { QIcon_bar_graph } from '@repo/icons/index'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { CAF_ANALYSIS_STATUS, HighlightOverviewResponse } from '../../../../model/caf.interface'
import { useCafLessonStatusV5 } from '../../../../modules/apis/student/lesson'
import { useCafAnalysisComponentListV5, useCafHighlightListV5 } from '../../../../modules/apis/webview/caf'
import { n_lesson_review, n_review } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { COLORS } from '../../../../modules/vars'
import CAFOverviewSideMenu from './CAFOverviewSideMenu'
import CAFOverviewHighlights from './content/CAFOverviewHighlights'
import CAFOverviewAccuracy from './content/accuracy'
import CAFOverviewComplexity from './content/complexity'
import CAFOverviewFluency from './content/fluency'
import CAFOverviewPronunciation from './content/pronunciation'
import CAFOverviewSummary from './content/summary'
import useCafLevelSidePopup from './popup/useCafLevelSidePopup'
import useHighlightEditPopup, { HighlightEditPopupComponent } from './popup/useHighlightEditPopup'

const NOT_READY_PLACEHOLDER_MAP = {
  [CAF_ANALYSIS_STATUS.STUDENT_NOSHOW]: n_review.noshow_ai,
  [CAF_ANALYSIS_STATUS.IN_PROGRESS]: n_lesson_review.ai_status_title1,
  [CAF_ANALYSIS_STATUS.ANALYSIS_NOT_ALLOWED]: n_lesson_review.ai_status_title2,
  [CAF_ANALYSIS_STATUS.RECORD_NOT_ALLOWED]: n_lesson_review.ai_status_title3,
  [CAF_ANALYSIS_STATUS.FREE_MATERIAL]: n_lesson_review.ai_status_title4,
}
const NOT_READY_SUB_PLACEHOLDER_MAP = {
  [CAF_ANALYSIS_STATUS.STUDENT_NOSHOW]: '',
  [CAF_ANALYSIS_STATUS.IN_PROGRESS]: '',
  [CAF_ANALYSIS_STATUS.ANALYSIS_NOT_ALLOWED]: n_lesson_review.ai_status_detail2,
  [CAF_ANALYSIS_STATUS.RECORD_NOT_ALLOWED]: n_lesson_review.ai_status_detail3,
  [CAF_ANALYSIS_STATUS.FREE_MATERIAL]: '',
}

const CAFOverview = ({ setResult }) => {
  const router = useRouter()
  const { tab, edit } = router.query
  const lessonId = router.query?.lessonId
  const [tabIndex, setTabIndex] = useState(0)
  const { data: status } = useCafLessonStatusV5()
  const { data: overallData, isLoading } = useCafAnalysisComponentListV5(
    +lessonId,
    status === CAF_ANALYSIS_STATUS.READY
  )
  const { showEditPopup, setShowEditPopup } = useHighlightEditPopup()

  useCafLevelSidePopup()

  const { data } = useCafHighlightListV5<HighlightOverviewResponse>({
    lessonId,
    recommended: true,
    show_details: true,
  })
  const [todoChecked, setTodoChecked] = useState<boolean>(false)
  useEffect(() => {
    data && setTodoChecked(data.check_caf)
  }, [data])

  useEffect(() => {
    if (Number(tab) >= 0) {
      setTabIndex(Number(tab))
    }
  }, [tab])

  const { t, isLocaleKO } = useTranslation()
  const availableMenus = overallData?.components.map((component) => component.component_tag)
  if (status !== CAF_ANALYSIS_STATUS.READY) {
    return (
      <div className="flex h-full w-full flex-1 flex-col items-center justify-start px-[20px] pt-[120px] sm:px-0">
        <div className="flex flex-col items-center">
          <QIcon_bar_graph color={COLORS.GRAY300} width={64} height={64} />
          <div className="mt-[20px] text-[18px] font-bold text-black sm:text-[20px]">
            {t(NOT_READY_PLACEHOLDER_MAP[status])}
          </div>
          <div className="mt-[12px] text-[16px] text-gray-500 sm:text-[20px]">
            {t(NOT_READY_SUB_PLACEHOLDER_MAP[status])}
          </div>
        </div>
      </div>
    )
  }
  if (!overallData) return null
  const componentId = overallData.components?.find((component) => component.component_tag === tabIndex)?.id || 0

  return (
    <div className="no-scrollbar flex flex-col items-center justify-start">
      <div className="flex h-[100%] w-full flex-row items-start justify-start">
        <div className="flex flex-row items-start justify-start">
          <CAFOverviewSideMenu
            activeIndex={tabIndex}
            setTabIndex={setTabIndex}
            todoChecked={todoChecked}
            setTodoChecked={setTodoChecked}
            availableMenus={availableMenus}
          />
          <div
            className={classNames(
              'no-scrollbar ml-0 overflow-y-scroll sm:ml-[44px] lg:w-[596px] xl:ml-[104px] xl:w-[688px]',
              {
                'w-full sm:w-[424px]': tabIndex !== 0,
                'sm:w-[352px]': tabIndex === 0,
              }
            )}
          >
            {tabIndex === 0 && (
              <CAFOverviewSummary
                overallData={overallData}
                isLoading={isLoading}
                status={status}
                todoChecked={todoChecked}
                setTodoChecked={setTodoChecked}
              />
            )}
            {tabIndex === 1 && <CAFOverviewComplexity componentId={componentId} />}
            {tabIndex === 2 && <CAFOverviewAccuracy componentId={componentId} />}
            {tabIndex === 3 && <CAFOverviewFluency componentId={componentId} />}
            {tabIndex === 4 && <CAFOverviewPronunciation componentId={componentId} />}
            {tabIndex === 5 && <CAFOverviewHighlights />}
          </div>
        </div>
      </div>
      <HighlightEditPopupComponent showEditPopup={showEditPopup} setShowEditPopup={setShowEditPopup} />
    </div>
  )
}

export default CAFOverview
